/* global chrome */

import Logger from '../../utils/logger';
import { getDeviceId, getChannelPlayable } from '../../utils/utils';
import {
  getSelectedLanguage,
  requestChromeCastSession,
  getTrackId,
  injectChromecastScript,
} from './utils';
import { AD_ROLL_TYPES, CHROMECAST_STATUSES } from '../../../common/constants';

const NAMESPACE = 'urn:x-cast:com.magine.cast.state';
const LOG_NAMESPACE = 'urn:x-cast:com.magine.cast.logs';
const REQUEST_SESSION_TIMEOUT_MS = 60000;

let _chromecastSDKPromise = null;
// Make sure the SDK is available when we try to use it
function initChromecastSDK() {
  if (!_chromecastSDKPromise) {
    _chromecastSDKPromise = new Promise((resolve) => {
      const timeoutId = window.setTimeout(() => resolve(false), 5000);

      window.__onGCastApiAvailable = (isLoaded, errorInfo) => {
        if (errorInfo) {
          Logger.debug('chromecastSDKAvailabilityPromise err', errorInfo);
        }
        window.clearTimeout(timeoutId);

        resolve(isLoaded);
      };

      // Inject Chromecast SDK script
      injectChromecastScript();
    });
  }

  return _chromecastSDKPromise;
}

export default class ChromecastSenderService {
  _player = null;
  _sharedInfo = {};

  _currentSession = null;
  _currentMedia = null;

  _volumeChangeRequests = 0;
  _trackChangeInProgress = false;

  _adPlaying = false;

  constructor(player) {
    this._player = player;
  }

  async _initializeChromeCastApi() {
    const isChromecastSDKAvailable = await initChromecastSDK();
    if (!isChromecastSDKAvailable) {
      throw new Error('chromecast SDK not available');
    }

    if (!chrome.cast) {
      throw new Error("chromecast is available but SDK wasn't loaded properly, probably due to tracking prevention");
    }

    return new Promise((resolve, reject) => {
      const sessionRequest = new chrome.cast.SessionRequest(
        this._player.model.chromecast.appId,
        [],
        REQUEST_SESSION_TIMEOUT_MS,
      );

      const apiConfig = new chrome.cast.ApiConfig(
        sessionRequest,
        (session) => {
          this._setCurrentSession(session);

          const media = this._currentSession.media[0];
          if (media) {
            // Chromecast was already playing and the session already contained a media
            Logger.debug('Chromecast sessionListener connecting to existing session', session.sessionId);
            this._setCurrentMedia(media);
          } else {
            // The user has just connected to Chromecast using the button in the browser (not the control
            // bar) and we want to load the media into the Chromecast receiver.
            // A media reference will be obtained in the onMediaDiscovered callback.
            Logger.debug('Chromecast sessionListener session', session.sessionId);
            this._transferPlaybackToChromecast();
          }
        },
        (status) => {
          Logger.debug('Chromecast _receiverListener', status, this._currentSession ? 'and session exists' : '');

          if (status === chrome.cast.ReceiverAvailability.AVAILABLE) {
            Logger.debug('Chromecast is available');

            this._player.model = this._player.model.merge({
              isChromeCastAvailable: true,
              isChromeCasting: !!this._currentSession,
            });
            this._player.dispatchEvent('ischromecastavailable', { detail: true });
          } else {
            Logger.debug('Chromecast unavailable - probably no receivers nearby');

            this._player.model = this._player.model.merge({
              isChromeCastAvailable: false,
              isChromeCasting: !!this._currentSession,
            });
            this._player.dispatchEvent('ischromecastavailable', { detail: false });
          }
        },
        chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
        chrome.cast.DefaultActionPolicy.CREATE_SESSION,
      );

      chrome.cast.initialize(
        apiConfig,
        () => {
          Logger.debug('Chromecast _onInitSuccess');
          resolve();
        },
        (e) => {
          Logger.debug('Chromecast _onInitFailed', e);
          reject();
        },
      );
    });
  }

  _sessionUpdateListener = () => {
    Logger.debug('Chromecast _sessionUpdateListener', this._currentSession.status);

    if (this._currentSession.status === chrome.cast.SessionStatus.STOPPED) {
      this._unsubscribeCurrentSession();
      this._currentSession = null;

      let castPlayerLastState;
      if (this._currentMedia) {
        castPlayerLastState = this._currentMedia.playerState;
        this._unsubscribeCurrentMedia();
        this._currentMedia = null;
        this._sharedInfo = {};
      }

      this._player.model = this._player.model.merge({
        isChromeCasting: false,
      });

      if (this._adPlaying) {
        this._player.dispatchEvent('adsDeactivated', {
          afterPreroll: this._adPlaying.adBreak.id === AD_ROLL_TYPES.preroll,
          afterPostroll: this._adPlaying.adBreak.id === AD_ROLL_TYPES.postroll,
        });
        this._adPlaying = false;
      }
      this._player.dispatchEvent('chromecastDeactivated:before', { wasChromeCasting: true, castPlayerLastState });
      this._player.dispatchEvent('chromecastDeactivated', { wasChromeCasting: true });
    }
  }

  _syncWithChromecast() {
    this._player.model = this._player.model.merge({
      chromecastPlayableId: this._sharedInfo.playableId,
    });

    if (!this._currentSession || !this._currentMedia || this._player._preflightInProgress) {
      return;
    }

    // Update player state
    const {
      media: mediaInfo,
      playerState,
      idleReason,
      items: itemQueue,
      currentItemId,
      loadingItemId,
    } = this._currentMedia;

    let isPlaying = false;
    let isBuffering = false;

    switch (playerState) {
      case CHROMECAST_STATUSES.PLAYING: {
        isPlaying = true;
        isBuffering = false;
        this._player.dispatchEvent('buffering', {
          isBuffering: false,
        });
        break;
      }
      case CHROMECAST_STATUSES.BUFFERING: {
        isBuffering = true;
        isPlaying = this._sharedInfo.isPlaying;
        this._player.dispatchEvent('buffering', {
          isBuffering: true,
        });
        break;
      }
      case CHROMECAST_STATUSES.PAUSED: {
        isPlaying = false;
        break;
      }
      case CHROMECAST_STATUSES.IDLE: {
        if (idleReason === 'FINISHED') {
          if (itemQueue?.length > 1) {
            const currentItemContentId = itemQueue?.find(item => item.itemId === currentItemId)?.media.contentId;
            const nextItem = itemQueue?.find(item => item.itemId === loadingItemId)?.media;
            if (currentItemContentId === this._player.model.viewable?.playableId) {
              this._player.dispatchEvent('chromecastPlayNext', {
                playableId: nextItem.contentId, viewableId: nextItem.customData.viewableId,
              });
            }
          } else {
            this._player.model = this._player.model.merge({
              isPlayedToEnd: true,
              isPlaying: false,
            });
            this._player.dispatchEvent('streamEnded');

            if (this._adPlaying) {
              this._player.dispatchEvent('adsDeactivated', {
                afterPreroll: this._adPlaying.adBreak.id === AD_ROLL_TYPES.preroll,
                afterPostroll: this._adPlaying.adBreak.id === AD_ROLL_TYPES.postroll,
              });
              this._adPlaying = false;
            }
          }
        }
        break;
      }
      default: break;
    }

    if (this._volumeChangeRequests === 0) {
      const { volume } = this._currentSession.receiver;

      // for some Chromecast devices muted === true but volume > 0
      const receiverVolume = volume.muted ? 0 : parseFloat(volume.level.toFixed(2), 10);

      if (receiverVolume !== this._player.model.volume) {
        this._player._saveVolume(receiverVolume);
      }
    }

    // Update Chromecast session
    const {
      receiver: {
        friendlyName,
      },
    } = this._currentSession;

    if (!this._player.model.isPlayingDaiAds && this._sharedInfo.isPlayingDaiAds) {
      this._player.dispatchEvent('dai:playing');
    }

    if (this._player.model.isPlayingDaiAds && !this._sharedInfo.isPlayingDaiAds) {
      this._player.dispatchEvent('dai:ended');
    }

    this._player.model = this._player.model.merge({
      assetId: this._sharedInfo.playableId,
      viewableId: this._sharedInfo.viewableId,
      currentTime: Math.round(this._sharedInfo.currentTime),
      streamDuration: this._sharedInfo.duration || 0,
      isPlaying,
      isBuffering,
      isPlayingDaiAds: this._sharedInfo.isPlayingDaiAds,
      isLive: this._sharedInfo.isLive,
    });

    if (!this._player.model.adsPlaying) {
      this._player.model = this._player.model.merge({
        currentTime: Math.round(this._sharedInfo.currentTime),
        streamDuration: this._sharedInfo.duration || 0,
      });
    }

    if (!this._sharedInfo.activeTrackIds || !mediaInfo?.tracks) {
      return;
    }

    if (!this._trackChangeInProgress) {
      const audioTracks = mediaInfo.tracks.filter(track => track.type === 'AUDIO');
      const audioLanguages = audioTracks.map(track => track.language);
      const textTracks = mediaInfo.tracks.filter(track => track.type === 'TEXT');
      const selectedAudio = getSelectedLanguage(this._sharedInfo.activeTrackIds, audioTracks);
      const selectedText = getSelectedLanguage(this._sharedInfo.activeTrackIds, textTracks);
      this._player.model = this._player.model.merge({
        audioTracks: audioLanguages,
        textTracks,
        videoTracks: ['auto'],
        selectedQuality: 'auto',
        selectedText,
        selectedAudio,
      });
    }

    if (this._adPlaying) {
      const currentTime = Math.round(this._sharedInfo.currentTime);
      const { whenSkippable } = this._adPlaying.breakStatus;
      const { duration } = this._adPlaying.ad;
      const secondsLeftToSkip = whenSkippable ? Math.max(whenSkippable - currentTime, 0) : -1;
      this._player.dispatchEvent('ontimeupdateads', {
        playerInfo: {
          currentTime,
          adsDuration: duration,
          secondsLeftToSkip,
        },
      });
    } else {
      this._player.dispatchEvent('chromecastOntimeupdate', {
        model: this._player.model,
        streamType: this._sharedInfo.isLive ? 'liveStream' : 'vod',
        isLive: this._sharedInfo.isLive,
        streamDuration: this._sharedInfo.duration || 0,
        friendlyName,

        audioTracks: this._player.model.audioTracks,
        videoTracks: ['auto'], // FIXME rename into qualities
        textTracks: this._player.model.textTracks.map(track => track.language),
        selectedText: this._player.model.selectedText,
        selectedAudio: this._player.model.selectedAudio,
      });
    }
  }

  _onMediaStatusUpdate = () => {
    if (!this._currentMedia || !this._currentMedia.media) {
      this._setCurrentMedia(this._currentSession.media[0]);
    }

    if (this._currentMedia.customData) {
      const customDataError = new Error('ChromecastError');
      customDataError.detail = {
        extra: {
          source: 'chromecast',
          response: JSON.stringify(this._currentMedia.customData),
        },
      };
      this._player._errorhandlingService?.onChromecastError(customDataError);
      return;
    }

    const { breakStatus } = this._currentMedia;
    if (breakStatus?.breakId) {
      const { breakClipId, breakId, whenSkippable } = breakStatus;
      const ad = this._currentMedia.media.breakClips.find(a => a.id === breakClipId);
      const adBreak = this._currentMedia.media.breaks.find(b => b.id === breakId);
      const adPosition = adBreak.breakClipIds.findIndex(b => b === breakClipId);
      const count = adBreak.breakClipIds.length;

      if (
        breakClipId !== this._adPlaying?.breakStatus?.breakClipId
        || breakId !== this._adPlaying?.breakStatus?.breakId
      ) {
        this._adPlaying = {
          breakStatus,
          ad,
          adBreak,
        };

        this._player.dispatchEvent('adsPlaying', {
          playerInfo: {
            currentTime: 0,
            seekRange: {
              start: 0,
              startAbsolute: 0,
              end: ad.duration,
              endAbsolute: ad.duration,
            },
          },
          adsCount: count,
          adsLeft: count - adPosition - 1,
          adTitle: ad.title,
          adHasLink: !!ad.clickThroughUrl,
        });

        this._player.dispatchEvent('chromecastAds', breakStatus);

        this._player.dispatchEvent('ontimeupdateads', {
          playerInfo: {
            currentTime: 0,
            adsDuration: ad.duration,
            secondsLeftToSkip: whenSkippable || -1,
          },
        });
      }
    } else if (this._adPlaying) {
      this._player.dispatchEvent('adsDeactivated', {
        afterPreroll: this._adPlaying.adBreak.id === AD_ROLL_TYPES.preroll,
        afterPostroll: this._adPlaying.adBreak.id === AD_ROLL_TYPES.postroll,
      });
      this._adPlaying = false;
    }

    if (this._currentSession.status === chrome.cast.SessionStatus.STOPPED) {
      this._unsubscribeCurrentMedia();
      this._currentMedia = null;
      this._sharedInfo = {};
    } else {
      this._syncWithChromecast();
    }

    Logger.debug('Chromecast _onMediaStatusUpdate', this._currentSession);
  }

  _unsubscribeCurrentMedia() {
    this._currentMedia.removeUpdateListener(this._onMediaStatusUpdate);
  }

  _setCurrentMedia = (media) => {
    Logger.debug('Chromecast _setCurrentMedia()', media);

    if (this._currentMedia) {
      this._unsubscribeCurrentMedia();
    }

    this._currentMedia = media;
    this._currentMedia.addUpdateListener(this._onMediaStatusUpdate);

    this.setReceiverVolume(this._player.model.volume);
  }

  _onReceiverMessage = (_namespace, message) => {
    const receiverMessage = message.includes('{') ? JSON.parse(message) : message;
    Logger.trace('Chromecast _onReceiverMessage() message', receiverMessage);

    if (receiverMessage.errors?.includes('PINCODE_NOT_SUBMITTED')) {
      this._player.dispatchEvent('entitlement:required-pincode');
    }

    this._sharedInfo = receiverMessage;
    this._syncWithChromecast();
  }

  _onReceiverLog = (_namespace, message) => {
    const { logLevel, msg } = JSON.parse(message);
    Logger.logWithLevel(logLevel || 4, 'MAGICAST:', msg || message);
  }

  _unsubscribeCurrentSession() {
    this._currentSession.removeUpdateListener(this._sessionUpdateListener);
    this._currentSession.removeMessageListener(this._onReceiverMessage);
    this._currentSession.removeMessageListener(this._onReceiverLog);
    this._currentSession.removeMediaListener(this._setCurrentMedia);
  }

  _setCurrentSession(newSession) {
    if (this._currentSession) {
      this._unsubscribeCurrentSession();
    } else {
      const { sessionId } = newSession;
      this._player.model = this._player.model.merge({
        isChromeCastAvailable: true,
        isChromeCasting: true,
      });
      this._player.dispatchEvent('chromecastActivated', { sessionId });
    }

    this._currentSession = newSession;
    this._currentSession.addUpdateListener(this._sessionUpdateListener);
    this._currentSession.addMessageListener(NAMESPACE, this._onReceiverMessage);
    this._currentSession.addMessageListener(LOG_NAMESPACE, this._onReceiverLog);
    this._currentSession.addMediaListener(this._setCurrentMedia);
    Logger.info('Chromecast _setCurrentSession', newSession);
  }

  _createMediaRequest() {
    const { model } = this._player;

    const { hlsManifestOnly } = getChannelPlayable(model.viewable);
    const mimeType = hlsManifestOnly ? 'application/x-mpegurl' : 'application/dash+xml';
    const mediaInfo = new chrome.cast.media.MediaInfo(model.assetId, mimeType);
    mediaInfo.streamType = model.isLive ? chrome.cast.media.StreamType.LIVE
      : chrome.cast.media.StreamType.BUFFERED;

    mediaInfo.customData = {
      accessToken: model.accessToken,
      authorizationKey: model.authorizationKey,
      entitlementToken: model.entitlementToken,
      senderDeviceId: getDeviceId(),
      initialAudioTrackLanguage: model.preferredAudio,
      initialTextTrackLanguage: model.preferredText,
      viewableId: model.viewableId,
      kGCKMetadataNlsKeyOptout: model.optout ? JSON.stringify(model.optout) : 'false',
    };
    mediaInfo.metadata = new chrome.cast.media.GenericMediaMetadata();
    mediaInfo.metadata.metadataType = chrome.cast.media.MetadataType.GENERIC;

    const request = new chrome.cast.media.LoadRequest(mediaInfo);
    request.autoplay = model.autoplay;

    if (model.currentTime && model.currentTime !== -1) {
      request.currentTime = model.currentTime;
    }

    Logger.debug('Chromecast _createMediaRequest', request);

    return request;
  }

  _onMediaError = (e) => {
    Logger.error('Chromecast onMediaError', e);
    if (e.details && e.details.type === 'LOAD_CANCELLED') return;

    const customDataError = new Error('ChromecastError');
    customDataError.detail = {
      response: e.details?.customData,
      extra: {
        source: 'chromecast',
        error: e,
      },
    };
    this._player._errorhandlingService?.onChromecastError(customDataError);
  }

  async editTracksInfo(tracksInfoRequest) {
    if (this._trackChangeInProgress) {
      return;
    }

    this._trackChangeInProgress = true;
    try {
      await new Promise((resolve, reject) => {
        this._currentMedia.editTracksInfo(tracksInfoRequest, resolve, reject);
      });
    } finally {
      this._trackChangeInProgress = false;
    }
  }

  async _transferPlaybackToChromecast() {
    try {
      const mediaRequest = this._createMediaRequest();

      Logger.debug('Loading to Chromecast ', mediaRequest);
      this._currentSession.loadMedia(mediaRequest, this._setCurrentMedia, this._onMediaError);

      return true;
    } catch (err) {
      Logger.error('Chromecast _transferPlaybackToChromecast()', err);
      this._player.dispatchEvent('chromecastDeactivated:before', { wasChromeCasting: false });
      this._player.dispatchEvent('chromecastDeactivated', { wasChromeCasting: false });
      return false;
    }
  }

  // ------------------------------- PUBLIC API -----------------------------------------

  async start() {
    try {
      await this._initializeChromeCastApi();
      Logger.debug('ChromecastSender service: started');
    } catch (e) {
      Logger.warn('Failed to initialize chromecast sender', e);
    }
  }

  /**
   * This is the method that gets called when the chromecast
   * icon in the control bar is clicked
   */
  async invokeChromecast() {
    const {
      isChromeCastAvailable,
    } = this._player.model;

    if (!isChromeCastAvailable) {
      return false;
    }

    if (!this._player.model.assetId) {
      return false;
    }

    // request new Chromecast session even if we have one to trigger the Chromecast popup
    const session = await requestChromeCastSession().catch((e) => {
      if (e.code !== 'cancel') { // 'cancel' means user just closed the Chromecast popup
        Logger.error('Chromecast requestChromeCastSession() failed', e);
      }

      return null;
    });

    if (!session) {
      return false;
    }

    this._setCurrentSession(session);

    return this._transferPlaybackToChromecast();
  }

  async changeTextTrackOrAudio(preferredId, type) {
    if (!this._currentMedia?.media?.tracks) {
      return;
    }

    let activeTracks = this._sharedInfo.activeTrackIds ? this._currentMedia.media.tracks.filter(track => (
      this._sharedInfo.activeTrackIds.includes(track.trackId)
    )) : [];

    if (activeTracks && activeTracks.length > 0) {
      activeTracks = activeTracks.filter(track => track.type !== type);
    }

    const newTrackId = getTrackId(this._currentMedia.media.tracks, preferredId, type);

    const activeTrackIds = [newTrackId, ...activeTracks.map(track => track && track.trackId)]
      .filter(e => e === 0 || e);

    const tracksInfoRequest = new chrome.cast.media.EditTracksInfoRequest(
      activeTrackIds,
      (message) => {
        Logger.debug('Chromecast success', message);
      },
      (e) => {
        Logger.debug('Chromecast error', e);

        const error = new Error('ChromecastError');
        error.detail = {
          extra: {
            source: 'chromecast',
            error: e,
          },
        };
        this._player._errorhandlingService?.onChromecastError(error);
      },
    );

    await this.editTracksInfo(tracksInfoRequest);
  }

  reloadCastSession(time) {
    const mediaRequest = this._createMediaRequest();

    if (time) {
      mediaRequest.currentTime = time + 5; // receiver app will rewind 5 seconds when receiving a new request.
    }

    this._currentSession.loadMedia(mediaRequest, this._setCurrentMedia, this._onMediaError);
  }

  pinCodePause() {
    if (!this._currentMedia) {
      return Promise.reject();
    }

    return new Promise((resolve, reject) => {
      const request = new chrome.cast.media.PauseRequest();
      request.customData = { pinCodeRequired: true };
      this._currentMedia.pause(request, resolve, reject);
    });
  }

  seek(time) {
    if (!this._currentMedia) {
      return this.reloadCastSession(time);
    }

    return new Promise((resolve, reject) => {
      const request = new chrome.cast.media.SeekRequest();
      request.currentTime = Number(time);
      this._sharedInfo.currentTime = Number(time);
      this._currentMedia.seek(request, resolve, reject);
    });
  }

  play() {
    if (this._currentMedia) {
      this._currentMedia.play();
      if (this._adPlaying) {
        this._player.dispatchEvent('playing', {
          playerInfo: {
            isPaused: false,
          },
        });
      }
    }
  }

  pause() {
    if (this._currentMedia) {
      this._currentMedia.pause();
      if (this._adPlaying) {
        this._player.dispatchEvent('adsPause');
      }
    } else {
      const time = 1;
      this.reloadCastSession(time);
    }
  }

  async setReceiverVolume(volume) {
    this._volumeChangeRequests += 1;
    try {
      await new Promise((resolve, reject) => {
        this._currentSession.setReceiverVolumeLevel(volume, resolve, reject);
      });
      this._player.dispatchEvent('volumechange', {});
    } finally {
      this._volumeChangeRequests -= 1;
    }
  }

  openAdWebsite() {
    const { clickThroughUrl } = this._adPlaying.ad || {};

    if (clickThroughUrl) {
      window.open(clickThroughUrl);
    }
  }

  skip() {
    this._currentSession.sendMessage('urn:x-cast:com.google.cast.media', {
      type: 'SKIP_AD',
      requestId: 1,
      mediaSessionId: this._currentMedia.mediaSessionId,
    });
  }
}
